import { Directive, EmbeddedViewRef, Input, OnDestroy, TemplateRef, ViewContainerRef, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { UserSessionService } from '../services/user-session.service';
import { assertTemplate, IfHasEntitlementContext } from './if-has-entitlement-context';

// eslint-disable-next-line @angular-eslint/directive-selector
@Directive({
	selector: '[ifHasEntitlement]',
	standalone: true
})
export class IfHasEntitlementDirective implements OnDestroy {
	private _viewContainer = inject(ViewContainerRef);
	private _userSessionService = inject(UserSessionService);

	private _context: IfHasEntitlementContext = new IfHasEntitlementContext();
	private _thenTemplateRef: TemplateRef<IfHasEntitlementContext> | null = null;
	private _elseTemplateRef: TemplateRef<IfHasEntitlementContext> | null = null;
	private _thenViewRef: EmbeddedViewRef<IfHasEntitlementContext> | null = null;
	private _elseViewRef: EmbeddedViewRef<IfHasEntitlementContext> | null = null;

	private _componentDestroyed$: Subject<boolean> = new Subject();

	@Input()
	set ifHasEntitlement(entitlement: string | { entitlementType: string; entitlementName: string }) {
		/** check the type */
		if (typeof entitlement !== 'string') {
			entitlement = `${entitlement.entitlementType}|${entitlement.entitlementName}`;
		}

		/** set the entitlement string */
		this._context.entitlement = entitlement;
		this._context.hasEntitlement = this._userSessionService.hasEntitlement(this._context.entitlement);
		this._updateView();
	}

	@Input('ifHasEntitlementAnd')
	set And(and: boolean) {
		this._context.additionalCondition = and;
		this._context.additionalConditionType = 'and';
		this._updateView();
	}

	@Input('ifHasEntitlementOr')
	set Or(or: boolean) {
		this._context.additionalCondition = or;
		this._context.additionalConditionType = 'or';
		this._updateView();
	}

	/**
	 * A template to show if the condition expression evaluates to true.
	 */
	@Input()
	set ifHasEntitlementThen(thenTemplateRef: TemplateRef<IfHasEntitlementContext> | null) {
		assertTemplate('ifHasEntitlementThen', thenTemplateRef);
		this._thenTemplateRef = thenTemplateRef;
		this._thenViewRef = null; // clear previous view if any.
		this._updateView();
	}

	/**
	 * A template to show if the condition expression evaluates to false.
	 */
	@Input()
	set ifHasEntitlementElse(elseTemplateRef: TemplateRef<IfHasEntitlementContext> | null) {
		assertTemplate('ifHasEntitlementElse', elseTemplateRef);
		this._elseTemplateRef = elseTemplateRef;
		this._elseViewRef = null; // clear previous view if any.
		this._updateView();
	}

	constructor() {
		const _templateRef = inject<TemplateRef<any>>(TemplateRef);

		this._thenTemplateRef = _templateRef;
	}

	ngOnDestroy(): void {
		this._componentDestroyed$.next(true);
		this._componentDestroyed$.complete();
	}

	private _updateView() {
		if (this._context.conditionsMet) {
			if (!this._thenViewRef) {
				this._viewContainer.clear();
				this._elseViewRef = null;
				if (this._thenTemplateRef) {
					this._thenViewRef = this._viewContainer.createEmbeddedView(this._thenTemplateRef, this._context);
				}
			}
		} else {
			if (!this._elseViewRef) {
				this._viewContainer.clear();
				this._thenViewRef = null;
				if (this._elseTemplateRef) {
					this._elseViewRef = this._viewContainer.createEmbeddedView(this._elseTemplateRef, this._context);
				}
			}
		}
	}
}
